/** Candle chart with one selected regime ribbon and a technical trend tape.
 * Mock fixtures only — no HMM calculation.
 *
 * Hierarchy: candles + restrained regime background → one ribbon
 * (Confirmed by default; Instant optional on the Regime tab) → trend tape
 * → volume. Shared date scale throughout.
 */
import { useMemo, useState } from "react";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { FixedChart } from "@/cockpit/FixedChart";
import { AXIS, GRID } from "@/cockpit/formatCockpit";
import { REGIME_COLORS } from "@/workspace/charts";

const TREND = { bullish: "#10B981", sideways: "#71717A", bearish: "#EF4444" };
const MARK = {
  "Quiet Range": "Q",
  "Bullish Trend": "B",
  "Bearish Trend": "S",
  Volatile: "V",
  transitioning: "T",
};
const TREND_MARK = { bullish: "▲", sideways: "■", bearish: "▼" };

function spansOf(series, key) {
  const out = [];
  series.forEach((r, i) => {
    const state = r[key];
    const last = out[out.length - 1];
    if (last && last.state === state) {
      last.to = i;
      last.end = r.date;
    } else {
      out.push({ state, from: i, to: i, start: r.date, end: r.date });
    }
  });
  return out;
}

function HatchDefs({ uid }) {
  return (
    <defs>
      <pattern id={`${uid}-hatch-QuietRange`} width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0 6 L6 0" stroke="#93C5FD" strokeWidth="0.7" />
      </pattern>
      <pattern id={`${uid}-hatch-BullishTrend`} width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0 0 L6 6" stroke="#10B981" strokeWidth="0.8" />
      </pattern>
      <pattern id={`${uid}-hatch-BearishTrend`} width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0 6 L6 0" stroke="#EF4444" strokeWidth="0.8" />
      </pattern>
      <pattern id={`${uid}-hatch-Volatile`} width="5" height="5" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="0.8" fill="#F59E0B" />
      </pattern>
      <pattern id={`${uid}-hatch-transitioning`} width="4" height="4" patternUnits="userSpaceOnUse">
        <path d="M0 2 H4" stroke="#71717A" strokeWidth="0.6" />
      </pattern>
    </defs>
  );
}

function AlignedRibbon({ spans, n, plotX, plotW, height, uid, kind, width }) {
  return (
    <svg width={width} height={height} className="block" data-testid={`regime-ribbon-${kind.toLowerCase()}`}>
      <HatchDefs uid={uid} />
      {spans.map((s, i) => {
        const x = plotX + (s.from / n) * plotW;
        const w = Math.max(1, ((s.to - s.from + 1) / n) * plotW);
        const color = REGIME_COLORS[s.state] || "#71717A";
        return (
          <g key={`${kind}-${s.start}-${i}`}>
            <title>{`${kind}: ${s.state} · ${s.start} → ${s.end}`}</title>
            <rect x={x} y={1} width={w} height={height - 2} fill={color} opacity="0.55" />
            <rect x={x} y={1} width={w} height={height - 2} fill={`url(#${uid}-hatch-${s.state.replace(/\s+/g, "")})`} opacity="0.35" />
            {w > 36 ? (
              <text x={x + 4} y={height / 2 + 3} fill="#E4E4E7" fontSize="8">
                {MARK[s.state] || "·"} {s.start.slice(5)}
              </text>
            ) : (
              <text x={x + w / 2} y={height / 2 + 3} textAnchor="middle" fill="#E4E4E7" fontSize="8">
                {MARK[s.state] || "·"}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function TrendTape({ spans, n, plotX, plotW, height, width }) {
  return (
    <svg width={width} height={height} className="block" data-testid="trend-tape">
      {spans.map((s, i) => {
        const x = plotX + (s.from / n) * plotW;
        const w = Math.max(1, ((s.to - s.from + 1) / n) * plotW);
        return (
          <g key={`tr-${s.start}-${i}`}>
            <title>{`Trend: ${s.state} · ${s.start} → ${s.end}`}</title>
            <rect x={x} y={1} width={w} height={height - 2} fill={TREND[s.state] || "#71717A"} opacity="0.5" />
            {w > 22 ? (
              <text x={x + 3} y={height / 2 + 3} fill="#E4E4E7" fontSize="8">
                {TREND_MARK[s.state] || "·"}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

export function PriceRegimeStack({
  series,
  height = 200,
  showVolume = true,
  showSma = true,
  detailed = false,
  allowRibbonToggle = false,
  ribbonAvailable = true,
  alignmentMessage = null,
  showTrendTape = true,
  smaKeys,
  live = false,
  testid,
}) {
  const [hover, setHover] = useState(null);
  const [ribbonKind, setRibbonKind] = useState("confirmed");
  const uid = `prg-${series[0]?.date || "x"}-${series.length}`;
  const instantSpans = useMemo(() => spansOf(series, "regimeState"), [series]);
  const confirmedSpans = useMemo(() => spansOf(series, "confirmedRegimeState"), [series]);
  const trendSpans = useMemo(() => spansOf(series, "trendState"), [series]);
  const ribbonSpans = ribbonKind === "instant" ? instantSpans : confirmedSpans;
  const last = series[series.length - 1];
  const disagrees = last && last.regimeState && last.confirmedRegimeState && last.regimeState !== last.confirmedRegimeState;
  const plotH = height;
  const smaToDraw = smaKeys || (showSma ? (detailed ? ["sma20", "sma50", "sma200"] : ["sma20", "sma50"]) : []);
  const drawRibbon = ribbonAvailable && series.length > 0 && series.every((r) => r.regimeState != null || r.confirmedRegimeState != null);

  return (
    <div data-testid={testid} className="min-w-0">
      {(allowRibbonToggle && drawRibbon) || disagrees ? (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
          {allowRibbonToggle ? (
            <div className="flex items-center gap-1.5" data-testid="ribbon-mode-toggle">
              <span className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Ribbon</span>
              {["confirmed", "instant"].map((k) => {
                const active = ribbonKind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setRibbonKind(k)}
                    data-testid={`ribbon-mode-${k}`}
                    className="text-[10px] px-2 py-0.5 rounded-sm border"
                    style={{
                      color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                      background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                      borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
                    }}
                  >
                    {k === "confirmed" ? "Confirmed" : "Instant"}
                  </button>
                );
              })}
            </div>
          ) : <span />}
          {disagrees ? (
            <StatusBadge
              tier="caution"
              text="Instant differs from confirmed"
              title={`Instant ${last.regimeState} · Confirmed ${last.confirmedRegimeState}`}
              testid="regime-disagreement-chip"
            />
          ) : null}
        </div>
      ) : null}

      <FixedChart height={plotH}>
        {(width) => {
          const padL = 44;
          const padR = 8;
          const padT = 8;
          const padB = 16;
          const w = Math.max(60, width - padL - padR);
          const h = plotH - padT - padB;
          const lows = series.map((r) => r.low);
          const highs = series.map((r) => r.high);
          const min = Math.min(...lows) * 0.995;
          const max = Math.max(...highs) * 1.005;
          const y = (v) => padT + h - ((v - min) / (max - min)) * h;
          const step = w / series.length;
          const bw = Math.max(1, Math.min(6, step * 0.55));
          const path = (key) => series
            .map((r, i) => (r[key] == null ? null : `${i === 0 || series[i - 1][key] == null ? "M" : "L"}${padL + i * step + step / 2},${y(r[key])}`))
            .filter(Boolean)
            .join(" ");
          const ticks = [0, 0.5, 1].map((f) => min + (max - min) * f);
          return (
            <svg
              width={width}
              height={plotH}
              role="img"
              aria-label="Price candles with historical regime context"
              onMouseLeave={() => setHover(null)}
              onMouseMove={(ev) => {
                const rect = ev.currentTarget.getBoundingClientRect();
                const x = ev.clientX - rect.left - padL;
                const i = Math.max(0, Math.min(series.length - 1, Math.floor((x / w) * series.length)));
                setHover(series[i]);
              }}
              onClick={(ev) => {
                const rect = ev.currentTarget.getBoundingClientRect();
                const x = ev.clientX - rect.left - padL;
                const i = Math.max(0, Math.min(series.length - 1, Math.floor((x / w) * series.length)));
                setHover(series[i]);
              }}
            >
              <HatchDefs uid={uid} />
              {drawRibbon ? ribbonSpans.map((s, i) => {
                const x = padL + (s.from / series.length) * w;
                const bw2 = ((s.to - s.from + 1) / series.length) * w;
                return (
                  <rect
                    key={`bg-${s.start}-${i}`}
                    x={x}
                    y={padT}
                    width={bw2}
                    height={h}
                    fill={REGIME_COLORS[s.state] || "#71717A"}
                    opacity="0.10"
                  />
                );
              }) : null}
              {ticks.map((t) => (
                <g key={t}>
                  <line x1={padL} x2={padL + w} y1={y(t)} y2={y(t)} stroke={GRID} strokeDasharray="3 3" />
                  <text x={padL - 5} y={y(t) + 3} textAnchor="end" fill={AXIS} fontSize="9">{t.toFixed(0)}</text>
                </g>
              ))}
              {series.map((r, i) => {
                const x = padL + i * step + step / 2;
                const up = r.close >= r.open;
                const c = up ? "#10B981" : "#EF4444";
                const top = y(Math.max(r.open, r.close));
                const bot = y(Math.min(r.open, r.close));
                return (
                  <g key={r.date}>
                    <line x1={x} x2={x} y1={y(r.high)} y2={y(r.low)} stroke={c} strokeWidth="1" />
                    <rect
                      x={x - bw / 2}
                      y={top}
                      width={bw}
                      height={Math.max(1, bot - top)}
                      fill={up ? c : "none"}
                      stroke={c}
                      strokeWidth="1"
                    />
                  </g>
                );
              })}
              {smaToDraw.includes("sma20") ? <path d={path("sma20")} fill="none" stroke="#FBBF24" strokeWidth="1" /> : null}
              {smaToDraw.includes("sma50") ? <path d={path("sma50")} fill="none" stroke="#93C5FD" strokeWidth="1" /> : null}
              {smaToDraw.includes("sma200") ? <path d={path("sma200")} fill="none" stroke="#A1A1AA" strokeWidth="1" strokeDasharray="4 3" /> : null}
              {hover ? (
                <line
                  x1={padL + series.findIndex((r) => r.date === hover.date) * step + step / 2}
                  x2={padL + series.findIndex((r) => r.date === hover.date) * step + step / 2}
                  y1={padT}
                  y2={plotH - padB}
                  stroke="var(--hoip-text-tertiary)"
                  strokeDasharray="3 3"
                />
              ) : null}
            </svg>
          );
        }}
      </FixedChart>

      {hover ? (
        <div
          className="text-[10px] font-mono-data mt-1 px-2 py-1.5 border"
          style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)", color: "var(--hoip-text-secondary)" }}
          data-testid="price-regime-tooltip"
        >
          <span>{hover.date}</span>
          {" · "}O {Number(hover.open).toFixed(2)} H {Number(hover.high).toFixed(2)} L {Number(hover.low).toFixed(2)} C {Number(hover.close).toFixed(2)}
          {drawRibbon ? ` · Instant ${hover.regimeState || "—"} · Confirmed ${hover.confirmedRegimeState || "—"}` : ""}
          {showTrendTape && hover.trendState ? ` · Trend ${hover.trendState}` : ""}
        </div>
      ) : (
        <div className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
          Hover a bar for date, OHLC, instant/confirmed regime and trend.
        </div>
      )}

      {drawRibbon ? (
        <>
          <FixedChart height={14}>
            {(width) => (
              <AlignedRibbon
                spans={ribbonSpans}
                n={series.length}
                plotX={44}
                plotW={Math.max(60, width - 52)}
                height={14}
                uid={`${uid}-${ribbonKind}`}
                kind={ribbonKind === "instant" ? "Instant" : "Confirmed"}
                width={width}
              />
            )}
          </FixedChart>
          <div className="text-[9px] mb-1" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="ribbon-kind-label">
            {ribbonKind === "instant"
              ? (live ? "Instant regime history · exact session-date alignment · not shown with the confirmed ribbon" : "Instant regime history · mock series · not shown with the confirmed ribbon")
              : (live ? "Confirmed regime history · exact session-date alignment" : "Confirmed regime history · 5-session majority fixture, not a live HMM run")}
            {" · Q Quiet · B Bullish · S Bearish · V Volatile"}
          </div>
        </>
      ) : (
        <p className="text-[11px] mt-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="regime-alignment-unavailable">
          {alignmentMessage || "Historical regime alignment unavailable"}
        </p>
      )}

      {showTrendTape ? (
        <>
          <FixedChart height={12}>
            {(width) => (
              <TrendTape
                spans={trendSpans}
                n={series.length}
                plotX={44}
                plotW={Math.max(60, width - 52)}
                height={12}
                width={width}
              />
            )}
          </FixedChart>
          <div className="text-[9px] mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>
            Technical trend tape
          </div>
        </>
      ) : (
        <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="trend-tape-unavailable">
          Not yet available from current cached data
        </p>
      )}

      {showVolume ? (
        <FixedChart height={44}>
          {(width) => {
            const padL = 44;
            const w = Math.max(60, width - 52);
            const step = w / series.length;
            const bw = Math.max(1, Math.min(6, step * 0.55));
            const vmax = Math.max(...series.map((r) => r.volume || 0), 1);
            return (
              <svg width={width} height={44} aria-label="Volume">
                {series.map((r, i) => {
                  const x = padL + i * step + step / 2;
                  const vh = ((r.volume || 0) / vmax) * 38;
                  return (
                    <rect
                      key={`v-${r.date}`}
                      x={x - bw / 2}
                      y={42 - vh}
                      width={bw}
                      height={Math.max(1, vh)}
                      fill={r.close >= r.open ? "#10B98155" : "#EF444455"}
                    />
                  );
                })}
              </svg>
            );
          }}
        </FixedChart>
      ) : null}

      <div className="flex flex-wrap gap-3 mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span style={{ color: "#E4E4E7" }}>▮ Candles</span>
        {smaToDraw.includes("sma20") ? <span style={{ color: "#FBBF24" }}>— SMA20</span> : null}
        {smaToDraw.includes("sma50") ? <span style={{ color: "#93C5FD" }}>— SMA50</span> : null}
        {smaToDraw.includes("sma200") ? <span style={{ color: "#A1A1AA" }}>-- SMA200</span> : null}
        {showVolume ? <span>Volume</span> : null}
        {showTrendTape ? <span style={{ color: TREND.bullish }}>▲ Bullish tape</span> : null}
        {showTrendTape ? <span>■ Sideways tape</span> : null}
        {showTrendTape ? <span style={{ color: TREND.bearish }}>▼ Bearish tape</span> : null}
      </div>
    </div>
  );
}
